import { create } from 'zustand'
import axios from 'axios'
import { sendToAnalytics } from '../../Analytics'
import { getAuthenticatedUsername } from '../../stores/useBootstrapStore'
import { triggerFor } from './paywallCopy'
import { firePurchasePixels } from './purchaseTracking'

// One store for the free-tier paywall: the display-only usage counters
// (GET /me/usage) and the upgrade dialog. Enforcement never happens here —
// the server refuses with a typed 402 and the interceptor in
// entrypoints/index.jsx (or the AI widget's onRequestError) calls
// openUpgradeDialog with that body.
let usageRequest = null

function track(event, data) {
    try {
        sendToAnalytics({ event, data })
        window.posthog?.capture?.(event, data)
    } catch (error) {
        // Analytics must never break the paywall.
    }
}

export const usePaywallStore = create((set, get) => ({
    usage: null,
    usageStatus: 'idle', // idle | loading | ready | failed
    dialog: { open: false, reason: null, resume: null },

    loadUsage: (force = false) => {
        const { usageStatus } = get()
        if (!force && (usageStatus === 'ready' || usageStatus === 'loading')) {
            return usageRequest || Promise.resolve(get().usage)
        }
        // Anonymous viewers (public graphs) have no usage: skip the request
        // instead of following /me/usage's login redirect.
        if (!getAuthenticatedUsername()) {
            set({ usage: null, usageStatus: 'ready' })
            return Promise.resolve(null)
        }
        set({ usageStatus: 'loading' })
        usageRequest = axios
            .get('/me/usage')
            .then(response => {
                set({ usage: response.data, usageStatus: 'ready' })
                return response.data
            })
            .catch(() => {
                set({ usageStatus: 'failed' })
                return null
            })
            .finally(() => {
                usageRequest = null
            })
        return usageRequest
    },

    openUpgradeDialog: (reason = {}) => {
        const { dialog } = get()
        const trigger = reason.trigger || triggerFor(reason.code)
        const nextReason = { ...reason, trigger }
        if (dialog.open) {
            set({ dialog: { ...dialog, reason: nextReason } })
            return
        }
        track('paywall_hit', { trigger, code: reason.code || 'MENU' })
        set({
            dialog: {
                open: true,
                reason: nextReason,
                resume:
                    typeof reason.resume === 'function' ? reason.resume : null,
            },
        })
    },

    // Only the dialog's own controls close it ("See plans" navigates away), so
    // every close is a decline.
    closeUpgradeDialog: () => {
        const { dialog } = get()
        if (dialog.open) {
            track('upgrade_dialog_dismissed', {
                trigger: dialog.reason && dialog.reason.trigger,
                code: (dialog.reason && dialog.reason.code) || 'MENU',
            })
        }
        set({ dialog: { open: false, reason: null, resume: null } })
    },

    // After an embedded checkout / plan change: the plan flipped server-side,
    // so the counters are re-read and the event recorded; the dialog itself
    // shows the success step and runs `dialog.resume` on Continue. Purchase
    // pixels fire for a new subscription only (a plan change is not a sale).
    // `code` is the paywall reason that brought the buyer to /upgrade, so a
    // purchase can be broken down by quota without joining back to the hit.
    // Test-site purchases still record upgrade_succeeded (dashboards filter
    // them) but never fire the ad pixels.
    completeUpgrade: ({
        plan,
        tier,
        method = 'card',
        amount,
        currency,
        fbEventId,
        direction,
        code,
        period,
        testSite = false,
    }) => {
        const value = Number.isFinite(amount) ? amount / 100 : undefined
        track('upgrade_succeeded', {
            plan,
            tier,
            method,
            value,
            currency,
            direction,
            code: code || 'MENU',
            period,
        })
        if (method !== 'plan-change' && !testSite) {
            firePurchasePixels({
                plan,
                amount,
                currency,
                username: getAuthenticatedUsername(),
                fbEventId,
            })
        }
        get().loadUsage(true)
    },
}))

// No paid plan and no trial: only the FREE tier counts, and an unknown tier
// (usage not loaded, or the read failed) never does.
export const selectIsFreeTier = state => state.usage?.tier === 'FREE'

export function trackPaywallEvent(event, data) {
    track(event, data)
}
